import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  MessageCircle, Heart, Flame, Sparkles, HandMetal, Frown,
  Filter, TrendingUp, Clock, Loader2, Share2, User
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { pageTransition, fadeUp } from "@/lib/animations";
import api from "@/lib/api";
import { toast } from "sonner";

const REACTIONS = [
  { type: "heart", icon: Heart, label: "Love" },
  { type: "fire", icon: Flame, label: "Fire" },
  { type: "mind_blown", icon: Sparkles, label: "Mind Blown" },
  { type: "clap", icon: HandMetal, label: "Clap" },
  { type: "sad", icon: Frown, label: "Relate" },
];

function ReactionButton({ type, icon: Icon, count, active, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={`reaction-${type}`}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
        active
          ? "bg-[#00FF88]/20 text-[#00FF88] border border-[#00FF88]/30"
          : "bg-white/5 text-white/40 hover:bg-white/10 hover:text-white border border-transparent"
      }`}
    >
      <Icon className="w-3.5 h-3.5" />
      {count > 0 && <span>{count}</span>}
    </button>
  );
}

function FeedCard({ item, onReact }) {
  const reactions = item.reactions || {};
  const userReaction = item.user_reaction;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-[24px] p-6 hover:border-white/15 transition-all"
      data-testid="feed-card"
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        {item.profile_image_url ? (
          <img 
            src={item.profile_image_url} 
            alt={item.display_name}
            className="w-9 h-9 rounded-full object-cover"
          />
        ) : (
          <div className="w-9 h-9 rounded-full bg-[#00FF88]/10 flex items-center justify-center">
            <User className="w-4 h-4 text-[#00FF88]" />
          </div>
        )}
        <div>
          <p className="text-sm font-medium text-white">{item.display_name}</p>
          <p className="text-xs text-white/30">
            {new Date(item.shared_at).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            })}
          </p>
        </div>
      </div>

      {/* Prompt */}
      <div className="bg-[#00FF88]/5 border border-[#00FF88]/10 rounded-xl p-4 mb-4">
        <p className="text-xs text-[#00FF88] uppercase tracking-wider mb-1">Prompt</p>
        <p className="text-sm text-white/70 leading-relaxed">{item.prompt_text}</p>
      </div>

      {/* Response */}
      <p className="text-base text-white leading-relaxed mb-5">{item.response}</p>

      {/* Reactions */}
      <div className="flex flex-wrap gap-2">
        {REACTIONS.map((r) => (
          <ReactionButton
            key={r.type}
            type={r.type}
            icon={r.icon}
            count={reactions[r.type] || 0}
            active={userReaction === r.type}
            onClick={() => onReact(item.share_id, r.type)}
          />
        ))}
      </div>
    </motion.div>
  );
}

export default function CommunityFeedPage() {
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("recent");
  const [total, setTotal] = useState(0);

  useEffect(() => {
    loadFeed();
  }, [sort]);

  const loadFeed = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/community/feed?sort=${sort}&limit=50`);
      setFeed(res.data.feed || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error("Failed to load community feed");
    } finally {
      setLoading(false);
    }
  };

  const handleReact = async (shareId, reactionType) => {
    try {
      const res = await api.post(`/community/react/${shareId}`, { reaction: reactionType });
      // Update local state
      setFeed((prev) =>
        prev.map((item) => {
          if (item.share_id !== shareId) return item;
          const newReactions = { ...item.reactions };
          if (res.data.status === "removed") {
            newReactions[reactionType] = Math.max(0, (newReactions[reactionType] || 0) - 1);
            return { ...item, reactions: newReactions, user_reaction: null };
          } else if (res.data.status === "added") {
            newReactions[reactionType] = (newReactions[reactionType] || 0) + 1;
            return { ...item, reactions: newReactions, user_reaction: reactionType };
          } else if (res.data.status === "switched") {
            // Decrement old, increment new
            const oldReaction = item.user_reaction;
            if (oldReaction) {
              newReactions[oldReaction] = Math.max(0, (newReactions[oldReaction] || 0) - 1);
            }
            newReactions[reactionType] = (newReactions[reactionType] || 0) + 1;
            return { ...item, reactions: newReactions, user_reaction: reactionType };
          }
          return item;
        })
      );
    } catch {
      toast.error("Please sign in to react");
    }
  };

  return (
    <motion.div variants={pageTransition} initial="hidden" animate="visible" className="p-6 md:p-10 max-w-[800px] mx-auto pb-24" data-testid="community-feed-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-[#00FF88]/10 flex items-center justify-center">
            <MessageCircle className="w-5 h-5 text-[#00FF88]" />
          </div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#00FF88] font-medium">Community</p>
        </div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: "Manrope" }}>
          Public Feed
        </h1>
        <p className="text-sm text-slate-400 mt-1">
          Shared reflections from the Black Box community
        </p>
      </div>

      {/* Sort Tabs */}
      <div className="flex gap-2 mb-6">
        <Button
          onClick={() => setSort("recent")}
          data-testid="sort-recent"
          variant={sort === "recent" ? "default" : "outline"}
          size="sm"
          className={`rounded-full ${
            sort === "recent"
              ? "bg-[#00FF88] text-black hover:bg-[#00CC6A]"
              : "border-white/10 text-slate-400"
          }`}
        >
          <Clock className="w-3.5 h-3.5 mr-1.5" /> Recent
        </Button>
        <Button
          onClick={() => setSort("popular")}
          data-testid="sort-popular"
          variant={sort === "popular" ? "default" : "outline"}
          size="sm"
          className={`rounded-full ${
            sort === "popular"
              ? "bg-[#00FF88] text-black hover:bg-[#00CC6A]"
              : "border-white/10 text-slate-400"
          }`}
        >
          <TrendingUp className="w-3.5 h-3.5 mr-1.5" /> Popular
        </Button>
      </div>

      {/* Feed */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <Loader2 className="w-8 h-8 text-[#00FF88] animate-spin" />
          <p className="text-sm text-slate-400">Loading community stories...</p>
        </div>
      ) : feed.length === 0 ? (
        <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-12 text-center">
          <MessageCircle className="w-10 h-10 text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2" style={{ fontFamily: "Manrope" }}>
            No stories yet
          </h3>
          <p className="text-sm text-slate-400 max-w-sm mx-auto">
            Be the first to share a reflection! Complete a daily prompt and share it with the community.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {feed.map((item) => (
            <FeedCard key={item.share_id} item={item} onReact={handleReact} />
          ))}
        </div>
      )}

      {/* Stats Footer */}
      {total > 0 && (
        <div className="text-center mt-8 pt-6 border-t border-white/[0.06]">
          <p className="text-xs text-slate-500">
            {total} {total === 1 ? "story" : "stories"} shared by the community
          </p>
        </div>
      )}
    </motion.div>
  );
}
